import React, { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Column } from "react-table";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { AuthSessionManager, useAuth, useHasRole } from "@homesusa/auth";
import {
  Format,
  PersistentRightModal,
  PersistentRightModalContext,
  RoleEmployee,
  RoleUser,
  RoleUserLabel,
  StatusIndicatorIcon
} from "@homesusa/core";
import { FormContext } from "@homesusa/form";
import {
  GridTable,
  GridProvider,
  GridToolbar,
  FetchDataDownloading,
  GridResponse,
  StatusPill,
  useFetchData
} from "@homesusa/grid-table";
import {
  AppContext,
  HeaderOutlet,
  HeaderTitle,
  SubmitButton,
  useTheme
} from "@homesusa/layout";
import {
  RoleUserPillVariant,
  UserStatus,
  UserStatusLabel,
  UserStatusColor
} from "core/enums";
import { UserDetail } from "core/interfaces";
import {
  getUsers,
  createUser,
  stopImpersonation,
  getImpersonationStatus
} from "core/services/user.service";
import { UserCreateForm } from "../components";
import { UserCreateFormProvider } from "../providers";
import {
  getImpersonationErrorMessage,
  useImpersonateUser,
  addReauthMarker
} from "../hooks";

function EmailConfirmedCell({
  confirmed,
  usePill
}: {
  confirmed: boolean;
  usePill: boolean;
}): JSX.Element {
  const label = confirmed ? "Yes" : "No";
  if (!usePill) {
    return <>{label}</>;
  }
  return (
    <StatusPill label={label} variant={confirmed ? "success" : "error"} />
  );
}

function RoleCell({
  roles,
  usePill
}: {
  roles: RoleUser[] | string[] | undefined;
  usePill: boolean;
}): JSX.Element {
  const roleList = roles ?? [];
  if (roleList.length === 0) {
    return <>?</>;
  }

  if (!usePill) {
    return <>{roleList.toString()}</>;
  }

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexWrap: "wrap",
        gap: 0.5,
        justifyContent: "center"
      }}
    >
      {roleList.map((role) => {
        const label = RoleUserLabel.get(role as RoleUser) ?? String(role);
        return (
          <StatusPill
            key={`${role}-${label}`}
            label={label}
            variant={RoleUserPillVariant[role as RoleUser] ?? "neutral"}
          />
        );
      })}
    </Box>
  );
}

function ActionsCell({
  row,
  isImpersonating,
  currentUserId,
  onImpersonate
}: Readonly<{
  row: { original: UserDetail };
  isImpersonating: boolean;
  currentUserId?: string;
  onImpersonate: (userId: string) => void;
}>): JSX.Element {
  if (isImpersonating) {
    return <></>;
  }

  const { emailConfirmed, isDeleted, id, roles, companies } = row.original;
  const roleList = (roles ?? []) as string[];
  const isCompanyExemptTarget =
    roleList.includes(RoleUser.MLSAdministrator) ||
    roleList.includes(RoleUser.Photographer);
  const blockedReason =
    id === currentUserId
      ? "You cannot impersonate yourself"
      : isDeleted
        ? "This user is deactivated"
        : !emailConfirmed
          ? "This user has not confirmed their email"
          : !isCompanyExemptTarget && companies === ""
            ? "This user does not belong to a company"
              : !isCompanyExemptTarget && companies === null
                ? "The company information could not be loaded"
            : undefined;

  return (
    <Tooltip title={blockedReason ?? ""}>
      <span>
        <Button
          size="small"
          disabled={blockedReason !== undefined}
          onClick={(event): void => {
            event.stopPropagation();
            onImpersonate(row.original.id);
          }}
        >
          Login as
        </Button>
      </span>
    </Tooltip>
  );
}

function createActionsColumn(
  isImpersonating: boolean,
  currentUserId: string | undefined,
  onImpersonate: (userId: string) => void
): Column<UserDetail> {
  return {
    Header: "Actions",
    id: "actions",
    disableSortBy: true,
    Cell: ({ row }: { row: { original: UserDetail } }): JSX.Element => (
      <ActionsCell
        row={row}
        isImpersonating={isImpersonating}
        currentUserId={currentUserId}
        onImpersonate={onImpersonate}
      />
    )
  };
}

function SaveButton({
  getUsers
}: {
  getUsers: (props: { pageSize: number; pageIndex: number }) => void;
}): JSX.Element {
  const { addAlert } = React.useContext(AppContext);
  const { formState } = React.useContext(FormContext);
  const { setShowRightModal } = React.useContext(PersistentRightModalContext);

  return (
    <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
      <SubmitButton
        onClick={async (): Promise<void> =>
          createUser(formState).then((): void => {
            addAlert({
              message: "The request was successfully created",
              variant: "success"
            });
            getUsers({ pageSize: 50, pageIndex: 0 });
            setShowRightModal(false);
          })
        }
      >
        Save
      </SubmitButton>
    </Box>
  );
}

function UserGrid(): JSX.Element {
  const { addAlert } = React.useContext(AppContext);
  const { isMlsAdministrator, hasEmployeeRole } = useHasRole();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = React.useState<boolean>(false);
  const { variant } = useTheme();
  const usePills = variant === "new";
  const onImpersonate = useImpersonateUser();
  const canManageUsers =
    isMlsAdministrator || hasEmployeeRole([RoleEmployee.CompanyAdmin]);
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id;
  const [isImpersonating, setIsImpersonating] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!canManageUsers) {
      return;
    }

    getImpersonationStatus()
      .then(({ isImpersonating }) => setIsImpersonating(isImpersonating))
      .catch(() => setIsImpersonating(false));
  }, [canManageUsers]);

  const defaultSortBy = "+firstName,+lastName";
  const columns: Array<Column<UserDetail>> = React.useMemo(
    () => [
      {
        Header: "First Name",
        accessor: (data: UserDetail): string => data.firstName
      },
      {
        Header: "Last Name",
        accessor: (data: UserDetail): string => data.lastName
      },
      {
        Header: "Username",
        accessor: (data: UserDetail): string => data.email,
        id: "email"
      },
      {
        Header: "Email Confirmed",
        accessor: (data: UserDetail): string =>
          data.emailConfirmed ? "Yes" : "No",
        cellSx: usePills ? { textAlign: "center" } : undefined,
        Cell: ({
          row
        }: {
          row: { original: UserDetail };
        }): JSX.Element => (
          <EmailConfirmedCell
            confirmed={row.original.emailConfirmed}
            usePill={usePills}
          />
        )
      },
      {
        Header: "Business Phone",
        accessor: (data: UserDetail): string =>
          Format.PhoneNumber(data.businessPhone) ?? "",
        disableSortBy: true
      },
      {
        Header: "Mobile Phone",
        accessor: (data: UserDetail): string =>
          Format.PhoneNumber(data.mobilePhone) ?? "",
        disableSortBy: true
      },
      {
        Header: "Created",
        accessor: (data: UserDetail): string =>
          Format.DateTime(data.sysCreatedOn),
        id: "sysCreatedOn"
      },
      {
        Header: "Last Activity",
        accessor: (data: UserDetail): string => Format.DateTime(data.lastLogin),
        id: "lastLogin"
      },
      {
        Header: "Role",
        accessor: (data: UserDetail): string => data.roles.toString(),
        id: "roleName",
        cellSx: usePills ? { textAlign: "center" } : undefined,
        Cell: ({
          row
        }: {
          row: { original: UserDetail };
        }): JSX.Element => (
          <RoleCell roles={row.original.roles} usePill={usePills} />
        )
      },
      ...(isMlsAdministrator
        ? [createActionsColumn(isImpersonating, currentUserId, onImpersonate)]
        : [])
    ],
    [usePills, isMlsAdministrator, isImpersonating, currentUserId, onImpersonate]
  );
  const [userStatus, setUserStatus] = React.useState<UserStatus>(
    Object.values(UserStatus).slice(0, 1)[0]
  );

  const getUser = ({ row }: { row: UserDetail }): void => {
    navigate(`/users/${row.id}`);
  };

  const getGridData = React.useCallback(
    async ({
      pageSize,
      pageIndex,
      sortBy,
      globalFilter,
      isForDownloading
    }: FetchDataDownloading): Promise<GridResponse<UserDetail>> => {
      return getUsers({
        top: pageSize,
        skip: pageIndex,
        searchBy: globalFilter,
        sortBy: sortBy ?? defaultSortBy,
        isForDownloading,
        status: userStatus
      });
    },
    [userStatus]
  );
  const { data, fetchData, fetchDataForDownloading } =
    useFetchData<UserDetail>(getGridData);

  const statusOptions = React.useMemo(() => {
    const options = Object.fromEntries(
      Object.values(UserStatus).map((status) => [
        status.toString(),
        UserStatusLabel.get(status) ?? ""
      ])
    );

    return hasEmployeeRole([RoleEmployee.CompanyAdmin])
      ? Object.fromEntries(
          Object.entries(options).filter(
            ([key]): boolean => key !== UserStatus.Archived.toString()
          )
        )
      : options;
  }, [hasEmployeeRole]);

  const headerTitle = <HeaderTitle title="Users" />;
  return (
    <Fragment>
      <GridProvider
        options={{ columns, data: data.data }}
        totalRows={data.total}
        fetchData={fetchData}
      >
        <HeaderOutlet>{headerTitle}</HeaderOutlet>
        <GridToolbar title={variant === "old" && headerTitle}>
          {variant === "new" && (
            <GridToolbar.Left>
              {canManageUsers && (
                <GridToolbar.SelectFilter
                  defaultValue={UserStatus.Active}
                  onChange={(value): void => setUserStatus(value as UserStatus)}
                  options={statusOptions}
                  label="Status"
                  startIcon={<StatusIndicatorIcon />}
                  statusColor={UserStatusColor}
                />
              )}
            </GridToolbar.Left>
          )}
          <GridToolbar.Right>
            {canManageUsers && (
              <GridToolbar.AddButton
                onClick={(): void => setShowAddModal(true)}
              />
            )}
            {canManageUsers && isImpersonating && (
              <Button
                size="small"
                onClick={(): void => {
                  stopImpersonation()
                    .then(({ redirectUrl }) => {
                      AuthSessionManager.deleteCookie();
                      const destinationUrl = addReauthMarker(redirectUrl);
                      if (destinationUrl) {
                        window.location.href = destinationUrl;
                      } else {
                        throw new Error("No destination url was returned");
                      }
                    })
                    .catch((error: unknown) => {
                      addAlert({
                        message: getImpersonationErrorMessage(error),
                        variant: "error"
                      });
                    });
                }}
              >
                Stop impersonation
              </Button>
            )}
            <GridToolbar.RefreshButton />
            {data.total > 0 && (
              <GridToolbar.ExportButtons
                sheetName="Users"
                onClick={fetchDataForDownloading}
                fileName="users"
              />
            )}
            <GridToolbar.ColumnVisibilityButton />
            {variant === "new" && <GridToolbar.SearchFilter />}
            {variant === "old" && (
              <>
                {canManageUsers && (
                  <GridToolbar.SelectFilter
                    defaultValue={UserStatus.Active}
                    onChange={(value): void =>
                      setUserStatus(value as UserStatus)
                    }
                    options={statusOptions}
                    label="Status"
                  />
                )}
                <GridToolbar.SearchFilter />
              </>
            )}
          </GridToolbar.Right>
        </GridToolbar>
        <GridTable onRowClick={getUser} />
        <PersistentRightModal
          title="New User"
          open={showAddModal}
          onChange={(value): void => setShowAddModal(value)}
        >
          <UserCreateFormProvider>
            <UserCreateForm />
            <SaveButton getUsers={fetchData} />
          </UserCreateFormProvider>
        </PersistentRightModal>
      </GridProvider>
    </Fragment>
  );
}

export default UserGrid;
